/**
 * IF-437: Scoring Lego Blocks — types and defaults for the modular scoring config.
 *
 * ScoringConfig lives inside company_config.config.scoring_config (JSONB).
 * The system is backward-compatible: when no config exists, DEFAULT_SCORING_CONFIG
 * provides sensible defaults for facilities management.
 */

export type ScoringDimensionId =
  | "activity_completion"      // Protocol checklist completion (was "checklist_completion")
  | "supply_synapse"           // Supply inventory health (was "supply_levels")
  | "facility_pulse"             // AI-derived facility health (was "facility_health")
  | "signal_stability"           // Incident-free operation from Trace/Signal
  | "protocol_adherence"         // Scheduled vs completed activities
  | "time_variance"              // Actual vs estimated duration

export interface ScoringDimension {
  enabled: boolean
  /** Raw weight 0–100. Normalized at compute time: score = Σ(dim * w) / Σ(w). */
  weight: number
}

export interface ScoringConfig {
  version: 1
  /** Master toggle — when false, score is not computed (returns null). */
  enabled: boolean
  dimensions: Record<ScoringDimensionId, ScoringDimension>
}

/**
 * Category-inherited colors for each dimension.
 * Dimensions inherit from their parent scoring category:
 *   taskTorque → teal (activity_completion, protocol_adherence, time_variance)
 *   propertyPulse → rose (facility_pulse, supply_synapse)
 *   signal → amber (signal_stability)
 */
export const SCORING_DIMENSION_COLORS: Record<
  ScoringDimensionId,
  { bar: string; text: string; accent: string; glow: string }
> = {
  activity_completion: {
    bar: "bg-teal-500/70",
    text: "text-teal-400",
    accent: "border-teal-400/25",
    glow: "shadow-teal-500/20",
  },
  protocol_adherence: {
    bar: "bg-teal-500/70",
    text: "text-teal-400",
    accent: "border-teal-400/25",
    glow: "shadow-teal-500/20",
  },
  time_variance: {
    bar: "bg-teal-500/70",
    text: "text-teal-400",
    accent: "border-teal-400/25",
    glow: "shadow-teal-500/20",
  },
  facility_pulse: {
    bar: "bg-rose-500/70",
    text: "text-rose-400",
    accent: "border-rose-400/25",
    glow: "shadow-rose-500/20",
  },
  supply_synapse: {
    bar: "bg-rose-500/70",
    text: "text-rose-400",
    accent: "border-rose-400/25",
    glow: "shadow-rose-500/20",
  },
  signal_stability: {
    bar: "bg-amber-500/70",
    text: "text-amber-400",
    accent: "border-amber-400/25",
    glow: "shadow-amber-500/20",
  },
}

/**
 * Human-readable metadata for each dimension (used in the settings UI).
 * Labels match their parent category grouping:
 *   Task Torque: Activity Completion, Protocol Adherence, Time Variance
 *   Property Pulse: Facility Health, Supply Levels
 *   Signal Fidelity: Signal Stability
 */
export const SCORING_DIMENSION_META: Record<
  ScoringDimensionId,
  { label: string; description: string; dataSource: string; parentCategory: string }
> = {
  activity_completion: {
    label: "Activity Completion",
    description: "Protocol checklist completion rate across facility activities.",
    dataSource: "clean_reports.checklist",
    parentCategory: "Task Torque",
  },
  protocol_adherence: {
    label: "Protocol Adherence",
    description: "Scheduled activities completed vs planned within expected windows.",
    dataSource: "clean_reports vs scheduled_tasks",
    parentCategory: "Task Torque",
  },
  time_variance: {
    label: "Time Variance",
    description: "Activity duration efficiency — actual vs estimated time.",
    dataSource: "clean_reports.duration",
    parentCategory: "Task Torque",
  },
  facility_pulse: {
    label: "Facility Health",
    description: "AI-derived facility condition from reported maintenance and observations.",
    dataSource: "gemini_analysis.facility_pulse",
    parentCategory: "Property Pulse",
  },
  supply_synapse: {
    label: "Supply Levels",
    description: "Supply inventory health from AI analysis and SupplySnap estimates.",
    dataSource: "supply_snap_estimate + gemini_analysis.inventory_level",
    parentCategory: "Property Pulse",
  },
  signal_stability: {
    label: "Signal Stability",
    description: "Operational stability measured by incident-free days from Trace.",
    dataSource: "trace_entries (incident, anomaly types)",
    parentCategory: "Signal Fidelity",
  },
}

/** Dimension display order — grouped by parent category. */
export const SCORING_DIMENSION_ORDER: ScoringDimensionId[] = [
  "activity_completion",
  "protocol_adherence",
  "time_variance",
  "facility_pulse",
  "supply_synapse",
  "signal_stability",
]

/**
 * Default config for facilities management.
 * Weights are raw (0–100) and auto-normalize to 100% at compute time.
 */
export const DEFAULT_SCORING_CONFIG: ScoringConfig = {
  version: 1,
  enabled: true,
  dimensions: {
    activity_completion: { enabled: true, weight: 30 },
    supply_synapse:      { enabled: true, weight: 20 },
    facility_pulse:        { enabled: true, weight: 20 },
    signal_stability:      { enabled: true, weight: 15 },
    protocol_adherence:    { enabled: true, weight: 10 },
    time_variance:         { enabled: true, weight: 5 },
  },
}

/** Parse raw JSONB value into a validated ScoringConfig, falling back to default. */
export function parseScoringConfig(raw: unknown): ScoringConfig {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return DEFAULT_SCORING_CONFIG
  }
  const r = raw as Record<string, unknown>

  if (r.version !== 1) return DEFAULT_SCORING_CONFIG
  if (typeof r.enabled !== "boolean") return DEFAULT_SCORING_CONFIG
  if (typeof r.dimensions !== "object" || r.dimensions == null) return DEFAULT_SCORING_CONFIG

  const dims = r.dimensions as Record<string, unknown>
  const parsed: Partial<Record<ScoringDimensionId, ScoringDimension>> = {}

  for (const key of SCORING_DIMENSION_ORDER) {
    const d = dims[key]
    if (d == null || typeof d !== "object" || Array.isArray(d)) {
      parsed[key] = DEFAULT_SCORING_CONFIG.dimensions[key]
      continue
    }
    const dObj = d as Record<string, unknown>
    parsed[key] = {
      enabled: typeof dObj.enabled === "boolean" ? dObj.enabled : true,
      weight: typeof dObj.weight === "number" && dObj.weight >= 0 ? dObj.weight : DEFAULT_SCORING_CONFIG.dimensions[key].weight,
    }
  }

  return {
    version: 1,
    enabled: r.enabled as boolean,
    dimensions: parsed as Record<ScoringDimensionId, ScoringDimension>,
  }
}
